import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, AlertCircle, Check } from 'lucide-react';
import { PaymentCondition } from '@/types/quote';
import { cn } from '@/lib/utils';

interface PaymentConditionsEditorProps {
  conditions: PaymentCondition[] | null;
  onChange: (conditions: PaymentCondition[]) => void;
}

export const PaymentConditionsEditor = ({ conditions, onChange }: PaymentConditionsEditorProps) => {
  const [localConditions, setLocalConditions] = useState<PaymentCondition[]>(
    conditions || []
  );

  useEffect(() => {
    setLocalConditions(conditions || []);
  }, [conditions]);

  const totalPercentage = localConditions.reduce((sum, c) => sum + (c.percentage || 0), 0);
  const isValid = totalPercentage === 100;

  const handleAdd = () => {
    const remaining = 100 - totalPercentage;
    const newConditions = [
      ...localConditions,
      { percentage: remaining > 0 ? remaining : 0, description: '' },
    ];
    setLocalConditions(newConditions);
    onChange(newConditions);
  };

  const handleRemove = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions);
    onChange(newConditions);
  };

  const handleUpdate = (index: number, field: keyof PaymentCondition, value: string | number) => {
    const newConditions = localConditions.map((c, i) => {
      if (i === index) {
        return { ...c, [field]: value };
      }
      return c;
    });
    setLocalConditions(newConditions);
    onChange(newConditions);
  };

  const loadPreset = (preset: 'full' | 'half' | 'thirds' | 'bess') => {
    let newConditions: PaymentCondition[] = [];
    
    switch (preset) {
      case 'full':
        newConditions = [{ percentage: 100, description: 'Bij opdrachtverstrekking' }];
        break;
      case 'half':
        newConditions = [
          { percentage: 50, description: 'Bij opdrachtverstrekking' },
          { percentage: 50, description: 'Bij oplevering' },
        ];
        break;
      case 'thirds':
        newConditions = [
          { percentage: 50, description: 'Bij opdrachtverstrekking' },
          { percentage: 30, description: 'Bij start werkzaamheden' },
          { percentage: 20, description: 'Bij oplevering' },
        ];
        break;
      case 'bess':
        newConditions = [
          { percentage: 50, description: 'Bij opdrachtverstrekking' },
          { percentage: 20, description: 'Bij start werkzaamheden (civiel / elektrotechnisch)' },
          { percentage: 20, description: 'Bij levering BESS op locatie' },
          { percentage: 10, description: 'Bij oplevering en inbedrijfstelling' },
        ];
        break;
    }
    
    setLocalConditions(newConditions);
    onChange(newConditions);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Betalingscondities</CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => loadPreset('full')}
            >
              100%
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => loadPreset('half')}
            >
              50/50
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => loadPreset('thirds')}
            >
              50/30/20
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => loadPreset('bess')}
            >
              BESS
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {localConditions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Geen betalingscondities. Klik hieronder of kies een preset.
          </p>
        ) : (
          <div className="space-y-2">
            {localConditions.map((condition, index) => (
              <div key={index} className="flex gap-2 items-center">
                <div className="w-20 relative">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={condition.percentage}
                    onChange={(e) => handleUpdate(index, 'percentage', parseInt(e.target.value) || 0)}
                    className="pr-6 text-right"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    %
                  </span>
                </div>
                <Input
                  value={condition.description}
                  onChange={(e) => handleUpdate(index, 'description', e.target.value)}
                  placeholder="Bijv. Bij opdrachtverstrekking"
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(index)}
                  className="text-destructive hover:text-destructive shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <Button variant="outline" size="sm" onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Termijn toevoegen
          </Button>
          
          <div className={cn(
            'flex items-center gap-1 text-sm font-medium',
            isValid ? 'text-primary' : 'text-destructive'
          )}>
            {isValid ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span>Totaal: {totalPercentage}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
